"use client";

import {
    type HTMLAttributes,
    type PropsWithChildren,
    type ReactElement,
    useEffect,
    useRef,
    useState,
} from "react";
import { twJoin, twMerge } from "tailwind-merge";

import { BREAKPOINTS, type DEVICE_BREAKPOINTS } from "@/globals";
import { useWindowSize } from "@/hooks/use-window-size.hook";
import { BUTTON_STYLES } from "@/styles/buttons";
import { TYPO_STYLES } from "@/styles/typography";
import { WRAPPER_STYLES } from "@/styles/wrapper";

type Options = {
    [DEVICE_BREAKPOINTS.min]: {
        count: number;
        gap: number;
        offset?: number;
        scrollAlignment: "center" | "start";
    };
    [DEVICE_BREAKPOINTS.xs]: {
        count: number;
        gap: number;
        offset?: number;
        scrollAlignment: "center" | "start";
    };
    [DEVICE_BREAKPOINTS.sm]: {
        count: number;
        gap: number;
        offset?: number;
        scrollAlignment: "center" | "start";
    };
    [DEVICE_BREAKPOINTS.md]: {
        count: number;
        gap: number;
        offset?: number;
        scrollAlignment: "center" | "start";
    };
    [DEVICE_BREAKPOINTS.lg]: {
        count: number;
        gap: number;
        offset?: number;
        scrollAlignment: "center" | "start";
    };
    [DEVICE_BREAKPOINTS.xl]: {
        count: number;
        gap: number;
        offset?: number;
        scrollAlignment: "center" | "start";
    };
};
type CarouselProps = {
    fullScroll?: boolean;
    itemsCount: number;
    onChange?: (index: number) => void;
    options: Options;
    scrollToCenter?: boolean;
    styleId: string;
};

export const CarouselView = ({
    styleId,
    fullScroll,
    options,
    scrollToCenter,
    itemsCount,
    onChange,
    className,
    children,
    ...nativeProps
}: CarouselProps & HTMLAttributes<HTMLDivElement>): ReactElement => {
    const sliderContainerRef = useRef<HTMLDivElement | null>(null);
    const sliderWrapperRef = useRef<HTMLDivElement | null>(null);
    const [hasSlides, setHasSlides] = useState<boolean | undefined>();
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        setHasSlides(
            (sliderWrapperRef.current &&
                sliderContainerRef.current &&
                sliderWrapperRef.current.clientWidth > sliderContainerRef.current.clientWidth) ||
                undefined,
        );
    }, [sliderContainerRef, sliderWrapperRef]);

    const handleDirectionClick = (direction: number): void => {
        if (sliderContainerRef.current && sliderWrapperRef.current) {
            const itemWidth = sliderWrapperRef.current.clientWidth / itemsCount;
            sliderContainerRef.current.scrollTo({
                behavior: "smooth",
                left:
                    sliderContainerRef.current.scrollLeft +
                    direction *
                        (fullScroll
                            ? Math.max(
                                  sliderContainerRef.current.clientWidth - itemWidth,
                                  itemWidth,
                              )
                            : itemWidth),
            });
        }
    };
    const handleIndexClick = (index: number): void => {
        if (sliderContainerRef.current && sliderWrapperRef.current) {
            const itemWidth = sliderWrapperRef.current.clientWidth / itemsCount;
            sliderContainerRef.current.scrollTo({
                behavior: "smooth",
                left: index * itemWidth,
            });
        }
    };

    const handleScroll = (): void => {
        if (sliderContainerRef.current && sliderWrapperRef.current) {
            const itemWidth = sliderWrapperRef.current.clientWidth / itemsCount;
            const index = Math.round(sliderContainerRef.current.scrollLeft / itemWidth);

            if (activeIndex !== index) {
                setActiveIndex(index);
                onChange?.(index);
            }
        }
    };

    useEffect(() => {
        if (scrollToCenter && sliderContainerRef.current && sliderWrapperRef.current) {
            sliderContainerRef.current.scrollTo({
                behavior: "instant",
                left:
                    (sliderWrapperRef.current.clientWidth -
                        sliderContainerRef.current.clientWidth) /
                    2,
            });
        }
    }, [scrollToCenter]);

    return (
        <div {...nativeProps} className={twJoin(WRAPPER_STYLES.VCenterCenter(), className)}>
            <style>{`
                .style-${styleId}-container {
				  	scroll-snap-type: x mandatory;
      				-webkit-overflow-scrolling: touch;
      				overflow-x: auto;
					width:100%;
  					scrollbar-width: none;
                }
				.style-${styleId}-wrapper {
				  	display: flex;
       				gap:${options[BREAKPOINTS.xs].gap}rem;
				 	width:${`calc(${(100 * itemsCount) / options[BREAKPOINTS.xs].count}% + ${(itemsCount / options[BREAKPOINTS.xs].count - 1) * options[BREAKPOINTS.xs].gap}rem)`};

					@media (min-width: ${BREAKPOINTS.sm}px) {
                        gap:${options[BREAKPOINTS.sm].gap}rem;
						width:${`calc(${(100 * itemsCount) / options[BREAKPOINTS.sm].count}% + ${(itemsCount / options[BREAKPOINTS.sm].count - 1) * options[BREAKPOINTS.xs].gap}rem)`};
                    }
                    @media (min-width: ${BREAKPOINTS.md}px) {
                        gap:${options[BREAKPOINTS.md].gap}rem;
						width:${`calc(${(100 * itemsCount) / options[BREAKPOINTS.md].count}% + ${(itemsCount / options[BREAKPOINTS.md].count - 1) * options[BREAKPOINTS.xs].gap}rem)`};
                    }
                    @media (min-width: ${BREAKPOINTS.lg}px) {
                        gap:${options[BREAKPOINTS.lg].gap}rem;
						width:${`calc(${(100 * itemsCount) / options[BREAKPOINTS.lg].count}% + ${(itemsCount / options[BREAKPOINTS.lg].count - 1) * options[BREAKPOINTS.xs].gap}rem)`};
                    }
					@media (min-width: ${BREAKPOINTS.xl}px) {
                        gap:${options[BREAKPOINTS.xl].gap}rem;
						width:${`calc(${(100 * itemsCount) / options[BREAKPOINTS.xl].count}% + ${(itemsCount / options[BREAKPOINTS.xl].count - 1) * options[BREAKPOINTS.xs].gap}rem)`};
                    }
                }
                .style-${styleId}-item {
                    scroll-snap-align: ${options[BREAKPOINTS.xs].scrollAlignment};
					flex:1;
                    overflow:hidden;
					@media (min-width: ${BREAKPOINTS.sm}px) {
                        scroll-snap-align: ${options[BREAKPOINTS.sm].scrollAlignment};
                    }
					@media (min-width: ${BREAKPOINTS.md}px) {
                        scroll-snap-align: ${options[BREAKPOINTS.md].scrollAlignment};
                    }
					@media (min-width: ${BREAKPOINTS.lg}px) {
                        scroll-snap-align: ${options[BREAKPOINTS.lg].scrollAlignment};
                    }
					@media (min-width: ${BREAKPOINTS.xl}px) {
                        scroll-snap-align: ${options[BREAKPOINTS.xl].scrollAlignment};
                    }
                }
                .style-${styleId}-dot {
                    height: 16px;
                    width: 16px;
                    background-color: #bbb;
                    border-radius: 50%;
                    display: inline-block;
                    transition: background-color 0.3s ease;
                }
                .style-${styleId}-dot.active {
                    background-color: #717171;
                   
                }
                .style-${styleId}-container::-webkit-scrollbar {
                    display: none; /* Optional: hides the scrollbar for a cleaner look */
                }
            `}</style>
            <div
                className={twMerge(`style-${styleId}-container`)}
                ref={sliderContainerRef}
                onScroll={handleScroll}
            >
                <div className={`style-${styleId}-wrapper`} ref={sliderWrapperRef}>
                    {children}
                </div>
            </div>
            {hasSlides && (
                <CarouselNavigationView
                    onPrevClick={(): void => handleDirectionClick(-1)}
                    onNextClick={(): void => handleDirectionClick(1)}
                >
                    <CarouselDotsView
                        onDotClick={handleIndexClick}
                        activeIndex={activeIndex}
                        itemsCount={itemsCount}
                        styleId={styleId}
                        options={options}
                    />
                </CarouselNavigationView>
            )}
        </div>
    );
};

type CarouselItemProps = {
    styleId: string;
};

export const CarouselItemView = ({
    children,
    styleId,
    className,
}: CarouselItemProps & HTMLAttributes<HTMLDivElement>): ReactElement => (
    <div className={twMerge(`style-${styleId}-item`, className)}>{children}</div>
);

export type CarouselNavigationProps = {
    onNextClick?: () => void;
    onPrevClick?: () => void;
};

const CarouselNavigationView = ({
    onPrevClick,
    onNextClick,
    children,
}: PropsWithChildren<CarouselNavigationProps>): ReactElement => {
    return (
        <div
            className={twMerge(
                WRAPPER_STYLES.HCenterRight(),
                WRAPPER_STYLES.HCenterCenter(BREAKPOINTS.sm),
                "gap-4 w-full",
            )}
        >
            <button
                type="button"
                onClick={(): void => onPrevClick?.()}
                className={twMerge(BUTTON_STYLES.plain(), TYPO_STYLES.icon(), "text-3xl")}
            >
                {String.fromCharCode(Number("0xe917"))}
            </button>
            {children}
            <button
                type="button"
                className={twMerge(BUTTON_STYLES.plain(), TYPO_STYLES.icon(), "text-3xl")}
                onClick={(): void => onNextClick?.()}
            >
                {String.fromCharCode(Number("0xe918"))}
            </button>
        </div>
    );
};

type CarouselDotsProps = {
    activeIndex?: number;
    itemsCount: number;
    onDotClick?: (index: number) => void;
    options: Options;
    styleId: string;
};

const CarouselDotsView = ({
    onDotClick: onClick,
    activeIndex,
    itemsCount,
    styleId,
    options,
}: CarouselDotsProps): ReactElement | null => {
    const windowSize = useWindowSize();
    const [countDots, setCountDots] = useState<number | undefined>();

    useEffect(() => {
        const b = windowSize?.breakpoint;
        if (b !== undefined) {
            setCountDots(itemsCount - options[b].count + 1);
        }
    }, [itemsCount, options, windowSize?.breakpoint]);

    if (countDots) {
        return (
            <div className="flex-row hidden sm:flex">
                {Array.from({ length: countDots }, (_, index) => (
                    <button
                        className="py-4 px-2 pt-6"
                        onClick={(): void => onClick?.(index)}
                        key={index}
                    >
                        <span
                            className={`style-${styleId}-dot ${activeIndex === index ? "active" : ""}`}
                        />
                    </button>
                ))}
            </div>
        );
    }
    return null;
};
